import { APP_INITIALIZER, FactoryProvider, InjectionToken, LOCALE_ID, Provider } from '@angular/core';

import { I18NEXT_INSTANCE, I18NEXT_SERVICE, I18NextLoadResult, ITranslationService } from 'angular-i18next';
import i18next, { BackendModule } from 'i18next';
import I18NextFetchBackend from 'i18next-fetch-backend';

import {
  EnvironmentService,
  I18N_NAMESPACE,
  PlatformService,
  StorageService,
  TRANSLATE_FN,
  WindowRefService,
  kebapToCamelCase
} from '@hdms/shared/util-core';

import { debugKeysOutputProcessor, getDebugKeysOutputProcessor } from './i18n-debug-output.processor';

export const I18NEXT_BACKEND = new InjectionToken('apps.website.i18nextBackend', {
  factory: () => I18NextFetchBackend
});

const I18N_DEFAULT_NS = 'website';

/**
 * This adapter method re-routes any localization calls to Angular `$localize` method to I18NextService.
 * It's required for resolving the @angular/localize dependency of NgBootstrap, but will also work for
 * anything else depending on @angular/localize package.
 */
export function ngLocalizeAdapterFactory(
  i18NextService: ITranslationService,
  { nativeWindow }: WindowRefService
): () => void {
  return () => {
    nativeWindow.$localize = ([key]: string[]) => {
      const sanitizedKey = kebapToCamelCase(key?.replace(/(:@@)([^:]*)(.*)/, '$2'));
      return i18NextService.t(sanitizedKey);
    };
  };
}

export function i18nextInit(
  i18nextService: ITranslationService,
  i18nextBackend: BackendModule,
  { languageTagLong: appLang, translationPath, pathToAssets, releaseVersion, isProduction }: EnvironmentService,
  winRef: WindowRefService,
  { isBrowser }: PlatformService,
  storageService: StorageService
): () => Promise<I18NextLoadResult> {
  // initial namespace used also as default and fallback
  const defaultNS = I18N_DEFAULT_NS;

  // For correct pluralization i18next expects the language to be in long languageTag (`fr-FR`) format:
  // https://www.i18next.com/how-to/faq#how-should-the-language-codes-be-formatted

  //for conveniently displaying new translation keys while developing, add 'en' as translation fallback
  const fallbackLng = isProduction ? null : 'en';
  const supportedLngs = isProduction ? [appLang] : [appLang, fallbackLng];

  const i18nPath = translationPath || `${pathToAssets}/assets/i18n`;
  // add releaseVersion as cache-buster param in case translations are retrieved from static S3 bucket path
  const cacheBuster = isBrowser && translationPath ? `?${releaseVersion}` : '';

  return () =>
    i18nextService
      .use(debugKeysOutputProcessor)
      .use(i18nextBackend)
      .init({
        supportedLngs,
        lng: appLang,
        fallbackLng,
        ns: [defaultNS],
        defaultNS,
        fallbackNS: defaultNS,
        // https://www.i18next.com/misc/json-format#i18next-json-v3
        compatibilityJSON: 'v3',
        keySeparator: '.',
        nsSeparator: ':',
        postProcess: getDebugKeysOutputProcessor(isBrowser, winRef, storageService),
        returnEmptyString: false,
        appendNamespaceToMissingKey: true,
        interpolation: {
          // since i18next v.21 `skipOnVariables` defaults to true, preventing translation nesting.
          // disable it globally to properly parse existing usages of `$t(some.translation.string)`
          skipOnVariables: false
        },
        load: 'currentOnly',
        initImmediate: isBrowser,
        backend: {
          // As translation filenames are suffixed with locale format using underscore (`en_US`),
          // dash in internal languageTagLong format needs to be replaced with underscore.
          loadPath: (lng: string[], ns: string[]) =>
            `${i18nPath}/${ns.toString()}.${lng.toString().replace('-', '_')}.json${cacheBuster}`
        }
      });
}

export const I18N_INIT_PROVIDERS: Provider[] = [
  {
    provide: APP_INITIALIZER,
    useFactory: i18nextInit,
    deps: [I18NEXT_SERVICE, I18NEXT_BACKEND, EnvironmentService, WindowRefService, PlatformService, StorageService],
    multi: true
  },
  {
    provide: APP_INITIALIZER,
    useFactory: ngLocalizeAdapterFactory,
    deps: [I18NEXT_SERVICE, WindowRefService],
    multi: true
  },
  {
    provide: I18N_NAMESPACE,
    useValue: I18N_DEFAULT_NS
  },
  {
    provide: LOCALE_ID,
    deps: [EnvironmentService],
    useFactory: (envService: EnvironmentService) => envService.languageTag
  },
  {
    provide: TRANSLATE_FN,
    deps: [I18NEXT_SERVICE],
    useFactory: (i18NextService: ITranslationService) => i18NextService.t.bind(i18NextService)
  }
];

/**
 * In SSR scenario always provide a new i18next instance, as otherwise same
 * global static i18next instance is being used, which can lead to race conditions
 * with unexpected language output for concurrent requests using different languages.
 */
export const I18NEXT_INSTANCE_PROVIDER: FactoryProvider = {
  provide: I18NEXT_INSTANCE,
  useFactory: () => i18next.createInstance()
};
